import React, { useCallback } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon, DragHandle as DragHandleIcon } from "@mui/icons-material";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Drag handle component
const DragHandle = React.memo(React.forwardRef((props, ref) => (
  <IconButton
    {...props.listeners}
    {...props.attributes}
    ref={ref}
    size="small"
    title="Drag to reorder"
    sx={{ cursor: 'grab', p: 0.5 }}
  >
    <DragHandleIcon fontSize="small" />
  </IconButton>
)));

// Sortable item component
const SortableItem = React.memo(React.forwardRef(({
  itemId,
  item,
  index,
  onRemove,
  showSwitches = false,
  onSessionDataChange,
  sessionData = {}
}, ref) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const numericId = itemId.replace(/[^0-9]/g, '');

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover'
        }
      }}
    >
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
        {item.label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {showSwitches && (
          <>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!sessionData[numericId]?.is_featured}
                  onChange={(e) => onSessionDataChange(numericId, {
                    ...sessionData[numericId],
                    is_featured: e.target.checked
                  })}
                />
              }
              label="Featured"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!sessionData[numericId]?.is_dark_mode}
                  onChange={(e) => onSessionDataChange(numericId, {
                    ...sessionData[numericId],
                    is_dark_mode: e.target.checked
                  })}
                />
              }
              label="Dark Mode"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
            />
          </>
        )}
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          sx={{ color: 'error.main', p: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        <DragHandle listeners={listeners} attributes={attributes} />
      </Box>
    </Box>
  );
}));

// Sortable list display with DndContext wrapper
const SortableItemList = ({
  items,
  sortOrder,
  onRemove,
  onSortEnd,
  showSwitches = false,
  onSessionDataChange,
  sessionData = {}
}) => {
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortOrder.findIndex((item) => item === active.id);
      const newIndex = sortOrder.findIndex((item) => item === over.id);
      const newItems = arrayMove(sortOrder, oldIndex, newIndex);
      onSortEnd(newItems);
    }
  }, [sortOrder, onSortEnd]);

  if (sortOrder.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        No items selected
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
      }}
    >
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={sortOrder} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sortOrder.map((itemId, index) => {
              const item = items.find(item => item.value === itemId);
              return item ? (
                <SortableItem
                  key={`item-${itemId}`}
                  itemId={itemId}
                  item={item}
                  index={index}
                  onRemove={onRemove}
                  showSwitches={showSwitches}
                  onSessionDataChange={onSessionDataChange}
                  sessionData={sessionData}
                />
              ) : null;
            })}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default SortableItemList;
